import type {
  GenerationBomBasis,
  GenerationBomItemBasis,
} from '../../types/generationImpact';
import {
  GENERATION_BOM_BASIS_HASH_VERSION,
  GENERATION_BOM_BASIS_MODEL_VERSION,
  GENERATION_BOM_BASIS_SCHEMA_VERSION,
} from '../../types/generationImpact';
import type { BomItem, Quote, SupplyChainPlan } from '../../types/supplyChain';
import {
  bomGenerationCapacityMw,
  bomItemGenerationCapacityMw,
  isGenerationBomItem,
  selectActiveBomItems,
} from '../../types/supplyChain';
import { hashGenerationImpactValue } from './integrity';

export type GenerationBomBasisPlan = Pick<
  SupplyChainPlan,
  'bom' | 'quotes' | 'version' | 'updatedAt'
>;

export interface GenerationBomBasisAlignment {
  alignmentStatus: 'aligned' | 'modified' | 'missing';
  currentPlanHash: string | null;
  alignmentNotes: string[];
}

const MAX_BOM_ITEMS = 1_000;
const MAX_ID_LENGTH = 256;
const MAX_TEXT_LENGTH = 4_000;
const SHA256 = /^[a-f\d]{64}$/i;

const ITEM_KEYS = [
  'bomItemId',
  'itemHash',
  'category',
  'name',
  'spec',
  'quantity',
  'unit',
  'status',
  'phaseId',
  'optionGroupId',
  'selectionMethod',
  'derivedFrom',
  'netNameplateMWPerUnit',
  'totalNetNameplateMW',
  'unitCostUsd',
  'totalCostUsd',
  'priceSource',
  'priceQuoteId',
  'leadTimeMonthsLow',
  'leadTimeMonthsHigh',
  'leadTimeSource',
  'requiredOnSiteDate',
  'expectedOnSiteDate',
  'awardedSupplierId',
  'quoteIds',
  'evidenceDocumentIds',
] as const;

const BASIS_KEYS = [
  'schemaVersion',
  'modelVersion',
  'hashVersion',
  'sourcePlanVersion',
  'sourcePlanUpdatedAt',
  'sourcePlanHash',
  'selectionMode',
  'capacityBasis',
  'solverBinding',
  'items',
  'totalNetNameplateMW',
  'basisHash',
] as const;

const ITEM_UNITS = new Set(['each', 'kVA', 'MW', 'MWh', 'm', 'm2', 'ton', 'm3', 'lot']);
const ITEM_STATUSES = new Set([
  'planned',
  'rfq-out',
  'quoted',
  'ordered',
  'in-transit',
  'on-site',
  'commissioned',
]);
const SELECTION_METHODS = new Set([
  'additive',
  'explicit',
  'lowest-grounded-cost',
  'first-unpriced',
]);
const DERIVED_FROM = new Set(['auto-template', 'manual', 'uploaded-bom']);
const PRICE_SOURCES = new Set(['industry-baseline', 'manual', 'quote', 'catalog']);
const LEAD_TIME_SOURCES = new Set(['industry-baseline', 'manual', 'quote', 'unknown']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const keys = Object.keys(value).sort();
  const sortedExpected = [...expected].sort();
  return keys.length === sortedExpected.length
    && keys.every((key, index) => key === sortedExpected[index]);
}

function requiredString(value: unknown, field: string, max = MAX_TEXT_LENGTH): string {
  if (typeof value !== 'string') throw new TypeError(`${field} must be a string`);
  const normalized = value.trim();
  if (normalized.length === 0 || normalized.length > max || normalized.includes('\0')) {
    throw new TypeError(`${field} must be a non-empty bounded inert string`);
  }
  return normalized;
}

function boundedString(value: unknown, field: string, max = MAX_TEXT_LENGTH): string {
  if (typeof value !== 'string') throw new TypeError(`${field} must be a string`);
  const normalized = value.trim();
  if (normalized.length > max || normalized.includes('\0')) {
    throw new TypeError(`${field} must be a bounded inert string`);
  }
  return normalized;
}

function nullableString(value: unknown, field: string, max = MAX_ID_LENGTH): string | null {
  if (value == null || value === '') return null;
  return requiredString(value, field, max);
}

function finiteNumber(
  value: unknown,
  field: string,
  min: number,
  max: number,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    throw new TypeError(`${field} must be finite and in [${min}, ${max}]`);
  }
  return Object.is(value, -0) ? 0 : value;
}

function nullableFiniteNumber(
  value: unknown,
  field: string,
  min: number,
  max: number,
): number | null {
  return value == null ? null : finiteNumber(value, field, min, max);
}

function uniqueSortedIds(values: readonly (string | null | undefined)[]): string[] {
  return [...new Set(values
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map(value => requiredString(value, 'evidence id', MAX_ID_LENGTH)))]
    .sort((left, right) => left < right ? -1 : left > right ? 1 : 0);
}

function quoteEvidenceDocumentIds(quote: Quote): string[] {
  return uniqueSortedIds([
    quote.documentId,
    quote.provenance?.sourceDocId,
    ...(quote.attachments ?? []).map(attachment => attachment.documentId),
  ]);
}

function selectionMethod(item: BomItem, allItems: readonly BomItem[]): GenerationBomItemBasis['selectionMethod'] {
  const optionGroupId = item.optionGroupId?.trim();
  if (!optionGroupId) return 'additive';
  if (item.isSelectedAlternative === true) return 'explicit';
  const members = allItems.filter(candidate => candidate.optionGroupId === item.optionGroupId);
  return members.some(candidate => (
    candidate.totalCostUsd != null && Number.isFinite(candidate.totalCostUsd)
  ))
    ? 'lowest-grounded-cost'
    : 'first-unpriced';
}

type GenerationBomItemHashPayload = Omit<GenerationBomItemBasis, 'itemHash'>;

function itemHashPayload(item: GenerationBomItemBasis): GenerationBomItemHashPayload {
  const { itemHash: _itemHash, ...payload } = item;
  return payload;
}

function basisHashPayload(basis: GenerationBomBasis): Omit<GenerationBomBasis, 'basisHash'> {
  const { basisHash: _basisHash, ...payload } = basis;
  return payload;
}

function projectItem(
  item: BomItem,
  allItems: readonly BomItem[],
  planQuotes: readonly Quote[],
): GenerationBomItemBasis {
  const quantity = finiteNumber(item.quantity, `${item.id}.quantity`, Number.MIN_VALUE, 1_000_000_000);
  if (item.powerPerUnitMw != null) {
    finiteNumber(item.powerPerUnitMw, `${item.id}.powerPerUnitMw`, Number.MIN_VALUE, 100_000);
  }
  const netCapacity = bomItemGenerationCapacityMw(item);
  const quotes = planQuotes.filter(quote => quote.bomItemId === item.id);
  const priceQuoteId = nullableString(item.priceProvenance?.quoteId, `${item.id}.priceQuoteId`);
  const quoteIds = uniqueSortedIds([
    priceQuoteId,
    ...quotes.map(quote => quote.id),
  ]);
  const evidenceDocumentIds = uniqueSortedIds([
    ...(item.attachments ?? []).map(attachment => attachment.documentId),
    ...(item.awardAttachments ?? []).map(attachment => attachment.documentId),
    ...quotes.flatMap(quoteEvidenceDocumentIds),
  ]);

  const payload: GenerationBomItemHashPayload = {
    bomItemId: requiredString(item.id, 'bomItemId', MAX_ID_LENGTH),
    category: 'power-generation',
    name: requiredString(item.name, `${item.id}.name`),
    spec: boundedString(item.spec, `${item.id}.spec`),
    quantity,
    unit: item.unit,
    status: item.status,
    phaseId: nullableString(item.phaseId, `${item.id}.phaseId`),
    optionGroupId: nullableString(item.optionGroupId, `${item.id}.optionGroupId`),
    selectionMethod: selectionMethod(item, allItems),
    derivedFrom: item.derivedFrom,
    netNameplateMWPerUnit: item.powerPerUnitMw ?? null,
    totalNetNameplateMW: netCapacity,
    unitCostUsd: nullableFiniteNumber(item.unitCostUsd, `${item.id}.unitCostUsd`, 0, 1e15),
    totalCostUsd: nullableFiniteNumber(item.totalCostUsd, `${item.id}.totalCostUsd`, 0, 1e18),
    priceSource: item.priceSource,
    priceQuoteId,
    leadTimeMonthsLow: finiteNumber(item.leadTimeMonthsLow, `${item.id}.leadTimeMonthsLow`, 0, 1_200),
    leadTimeMonthsHigh: finiteNumber(item.leadTimeMonthsHigh, `${item.id}.leadTimeMonthsHigh`, 0, 1_200),
    leadTimeSource: item.leadTimeSource,
    requiredOnSiteDate: nullableString(item.requiredOnSiteDate, `${item.id}.requiredOnSiteDate`, 64),
    expectedOnSiteDate: nullableString(item.expectedOnSiteDate, `${item.id}.expectedOnSiteDate`, 64),
    awardedSupplierId: nullableString(item.awardedSupplierId, `${item.id}.awardedSupplierId`),
    quoteIds,
    evidenceDocumentIds,
  };
  if (payload.leadTimeMonthsLow > payload.leadTimeMonthsHigh) {
    throw new TypeError(`${item.id}.leadTimeMonthsLow cannot exceed leadTimeMonthsHigh`);
  }
  return {
    ...payload,
    itemHash: hashGenerationImpactValue(payload),
  };
}

/**
 * Freeze the canonical active generation procurement rows into a scenario.
 * The returned capacity remains net-nameplate reference data; no solver field
 * is read or written by this helper.
 */
export function buildGenerationBomBasis(plan: GenerationBomBasisPlan): GenerationBomBasis {
  if (!plan || !Array.isArray(plan.bom) || !Array.isArray(plan.quotes)) {
    throw new TypeError('A supply-chain plan with BOM and quote arrays is required');
  }
  const activeItems = selectActiveBomItems(plan.bom).filter(isGenerationBomItem);
  if (activeItems.length === 0) {
    throw new TypeError('The supply-chain plan has no active generation BOM items');
  }
  if (activeItems.length > MAX_BOM_ITEMS) {
    throw new TypeError(`Generation BOM basis cannot exceed ${MAX_BOM_ITEMS} active items`);
  }
  const items = activeItems.map(item => projectItem(item, plan.bom, plan.quotes));
  const sourcePlanVersion = finiteNumber(plan.version, 'sourcePlanVersion', 1, 1_000_000);
  if (!Number.isInteger(sourcePlanVersion)) {
    throw new TypeError('sourcePlanVersion must be an integer');
  }
  const sourcePlanUpdatedAt = requiredString(plan.updatedAt, 'sourcePlanUpdatedAt', 64);
  if (!Number.isFinite(Date.parse(sourcePlanUpdatedAt))) {
    throw new TypeError('sourcePlanUpdatedAt must be an ISO-compatible timestamp');
  }

  const payload: Omit<GenerationBomBasis, 'basisHash'> = {
    schemaVersion: GENERATION_BOM_BASIS_SCHEMA_VERSION,
    modelVersion: GENERATION_BOM_BASIS_MODEL_VERSION,
    hashVersion: GENERATION_BOM_BASIS_HASH_VERSION,
    sourcePlanVersion,
    sourcePlanUpdatedAt,
    sourcePlanHash: hashGenerationImpactValue(items),
    selectionMode: 'active-generation-items',
    capacityBasis: 'net-nameplate-reference-only',
    solverBinding: 'none',
    items,
    totalNetNameplateMW: bomGenerationCapacityMw(plan),
  };
  const basis: GenerationBomBasis = {
    ...payload,
    basisHash: hashGenerationImpactValue(payload),
  };
  const errors = validateGenerationBomBasis(basis);
  if (errors.length > 0) {
    throw new TypeError(`Generation BOM basis is invalid: ${errors[0]}`);
  }
  return basis;
}

function validateStringArray(value: unknown, path: string, errors: string[]): void {
  if (!Array.isArray(value) || value.length > MAX_BOM_ITEMS) {
    errors.push(`${path} must be a bounded string array`);
    return;
  }
  const normalized: string[] = [];
  value.forEach((entry, index) => {
    try {
      normalized.push(requiredString(entry, `${path}[${index}]`, MAX_ID_LENGTH));
    } catch (error) {
      errors.push((error as Error).message);
    }
  });
  if (new Set(normalized).size !== normalized.length) {
    errors.push(`${path} must not contain duplicate ids`);
  }
  const sorted = [...normalized].sort((left, right) => left < right ? -1 : left > right ? 1 : 0);
  if (sorted.some((entry, index) => entry !== normalized[index])) {
    errors.push(`${path} must use deterministic lexical order`);
  }
}

function validateNullableString(value: unknown, path: string, errors: string[]): void {
  if (value === null) return;
  try {
    requiredString(value, path, MAX_ID_LENGTH);
  } catch (error) {
    errors.push((error as Error).message);
  }
}

function validateNumber(
  value: unknown,
  path: string,
  min: number,
  max: number,
  errors: string[],
): void {
  try {
    finiteNumber(value, path, min, max);
  } catch (error) {
    errors.push((error as Error).message);
  }
}

function validateNullableNumber(
  value: unknown,
  path: string,
  min: number,
  max: number,
  errors: string[],
): void {
  if (value === null) return;
  validateNumber(value, path, min, max, errors);
}

function validateItem(value: unknown, index: number): string[] {
  const path = `items[${index}]`;
  if (!isRecord(value)) return [`${path} must be an object`];
  const errors: string[] = [];
  if (!hasExactKeys(value, ITEM_KEYS)) errors.push(`${path} must contain only the versioned allowlisted fields`);
  try { requiredString(value.bomItemId, `${path}.bomItemId`, MAX_ID_LENGTH); } catch (error) { errors.push((error as Error).message); }
  try { requiredString(value.name, `${path}.name`); } catch (error) { errors.push((error as Error).message); }
  try { boundedString(value.spec, `${path}.spec`); } catch (error) { errors.push((error as Error).message); }
  if (value.category !== 'power-generation') errors.push(`${path}.category must be power-generation`);
  if (typeof value.unit !== 'string' || !ITEM_UNITS.has(value.unit)) errors.push(`${path}.unit is unsupported`);
  if (typeof value.status !== 'string' || !ITEM_STATUSES.has(value.status)) errors.push(`${path}.status is unsupported`);
  if (typeof value.selectionMethod !== 'string' || !SELECTION_METHODS.has(value.selectionMethod)) errors.push(`${path}.selectionMethod is unsupported`);
  if (typeof value.derivedFrom !== 'string' || !DERIVED_FROM.has(value.derivedFrom)) errors.push(`${path}.derivedFrom is unsupported`);
  if (typeof value.priceSource !== 'string' || !PRICE_SOURCES.has(value.priceSource)) errors.push(`${path}.priceSource is unsupported`);
  if (typeof value.leadTimeSource !== 'string' || !LEAD_TIME_SOURCES.has(value.leadTimeSource)) errors.push(`${path}.leadTimeSource is unsupported`);
  validateNumber(value.quantity, `${path}.quantity`, Number.MIN_VALUE, 1_000_000_000, errors);
  validateNullableNumber(value.netNameplateMWPerUnit, `${path}.netNameplateMWPerUnit`, Number.MIN_VALUE, 100_000, errors);
  validateNullableNumber(value.totalNetNameplateMW, `${path}.totalNetNameplateMW`, Number.MIN_VALUE, 1e14, errors);
  validateNullableNumber(value.unitCostUsd, `${path}.unitCostUsd`, 0, 1e15, errors);
  validateNullableNumber(value.totalCostUsd, `${path}.totalCostUsd`, 0, 1e18, errors);
  validateNumber(value.leadTimeMonthsLow, `${path}.leadTimeMonthsLow`, 0, 1_200, errors);
  validateNumber(value.leadTimeMonthsHigh, `${path}.leadTimeMonthsHigh`, 0, 1_200, errors);
  if (typeof value.leadTimeMonthsLow === 'number'
    && typeof value.leadTimeMonthsHigh === 'number'
    && value.leadTimeMonthsLow > value.leadTimeMonthsHigh) {
    errors.push(`${path}.leadTimeMonthsLow cannot exceed leadTimeMonthsHigh`);
  }
  validateNullableString(value.phaseId, `${path}.phaseId`, errors);
  validateNullableString(value.optionGroupId, `${path}.optionGroupId`, errors);
  validateNullableString(value.priceQuoteId, `${path}.priceQuoteId`, errors);
  validateNullableString(value.requiredOnSiteDate, `${path}.requiredOnSiteDate`, errors);
  validateNullableString(value.expectedOnSiteDate, `${path}.expectedOnSiteDate`, errors);
  validateNullableString(value.awardedSupplierId, `${path}.awardedSupplierId`, errors);
  validateStringArray(value.quoteIds, `${path}.quoteIds`, errors);
  validateStringArray(value.evidenceDocumentIds, `${path}.evidenceDocumentIds`, errors);

  if (typeof value.netNameplateMWPerUnit === 'number' && typeof value.quantity === 'number') {
    const expected = value.netNameplateMWPerUnit * value.quantity;
    if (typeof value.totalNetNameplateMW !== 'number'
      || Math.abs(value.totalNetNameplateMW - expected) > Math.max(1, expected) * 1e-12) {
      errors.push(`${path}.totalNetNameplateMW must equal quantity × netNameplateMWPerUnit`);
    }
  } else if (value.netNameplateMWPerUnit === null && value.totalNetNameplateMW !== null) {
    errors.push(`${path}.totalNetNameplateMW must be null without a per-unit net nameplate`);
  }

  if (typeof value.itemHash !== 'string' || !SHA256.test(value.itemHash)) {
    errors.push(`${path}.itemHash must be a SHA-256 digest`);
  } else {
    try {
      const expected = hashGenerationImpactValue(itemHashPayload(value as unknown as GenerationBomItemBasis));
      if (value.itemHash !== expected) errors.push(`${path}.itemHash does not match its allowlisted item payload`);
    } catch (error) {
      errors.push(`${path}.itemHash could not be verified: ${(error as Error).message}`);
    }
  }
  return errors;
}

/** Validate versions, bounds, exact allowlisted shape, and every nested hash. */
export function validateGenerationBomBasis(value: unknown): readonly string[] {
  if (!isRecord(value)) return ['generation BOM basis must be an object'];
  const errors: string[] = [];
  if (!hasExactKeys(value, BASIS_KEYS)) errors.push('generation BOM basis must contain only the versioned allowlisted fields');
  if (value.schemaVersion !== GENERATION_BOM_BASIS_SCHEMA_VERSION) errors.push('schemaVersion is unsupported');
  if (value.modelVersion !== GENERATION_BOM_BASIS_MODEL_VERSION) errors.push('modelVersion is unsupported');
  if (value.hashVersion !== GENERATION_BOM_BASIS_HASH_VERSION) errors.push('hashVersion is unsupported');
  if (value.selectionMode !== 'active-generation-items') errors.push('selectionMode is unsupported');
  if (value.capacityBasis !== 'net-nameplate-reference-only') errors.push('capacityBasis must remain net-nameplate-reference-only');
  if (value.solverBinding !== 'none') errors.push('solverBinding must remain none');
  validateNumber(value.sourcePlanVersion, 'sourcePlanVersion', 1, 1_000_000, errors);
  if (typeof value.sourcePlanVersion === 'number' && !Number.isInteger(value.sourcePlanVersion)) {
    errors.push('sourcePlanVersion must be an integer');
  }
  try {
    const updatedAt = requiredString(value.sourcePlanUpdatedAt, 'sourcePlanUpdatedAt', 64);
    if (!Number.isFinite(Date.parse(updatedAt))) errors.push('sourcePlanUpdatedAt must be an ISO-compatible timestamp');
  } catch (error) {
    errors.push((error as Error).message);
  }

  const items = Array.isArray(value.items) ? value.items : [];
  if (!Array.isArray(value.items) || items.length === 0 || items.length > MAX_BOM_ITEMS) {
    errors.push(`items must contain between 1 and ${MAX_BOM_ITEMS} active generation rows`);
  }
  items.forEach((item, index) => errors.push(...validateItem(item, index)));
  const ids = items
    .map(item => isRecord(item) && typeof item.bomItemId === 'string' ? item.bomItemId : null)
    .filter((id): id is string => id !== null);
  if (new Set(ids).size !== ids.length) errors.push('items must not contain duplicate bomItemId values');

  validateNullableNumber(value.totalNetNameplateMW, 'totalNetNameplateMW', Number.MIN_VALUE, 1e14, errors);
  const knownCapacities = items
    .map(item => isRecord(item) && typeof item.totalNetNameplateMW === 'number'
      ? item.totalNetNameplateMW
      : null)
    .filter((capacity): capacity is number => capacity !== null);
  const expectedTotal = knownCapacities.length > 0
    ? knownCapacities.reduce((sum, capacity) => sum + capacity, 0)
    : null;
  if (expectedTotal === null ? value.totalNetNameplateMW !== null : (
    typeof value.totalNetNameplateMW !== 'number'
    || Math.abs(value.totalNetNameplateMW - expectedTotal) > Math.max(1, expectedTotal) * 1e-12
  )) {
    errors.push('totalNetNameplateMW must equal the sum of known active item net nameplates');
  }

  if (typeof value.sourcePlanHash !== 'string' || !SHA256.test(value.sourcePlanHash)) {
    errors.push('sourcePlanHash must be a SHA-256 digest');
  } else {
    try {
      if (value.sourcePlanHash !== hashGenerationImpactValue(items)) {
        errors.push('sourcePlanHash does not match the active generation item projection');
      }
    } catch (error) {
      errors.push(`sourcePlanHash could not be verified: ${(error as Error).message}`);
    }
  }
  if (typeof value.basisHash !== 'string' || !SHA256.test(value.basisHash)) {
    errors.push('basisHash must be a SHA-256 digest');
  } else {
    try {
      const expected = hashGenerationImpactValue(basisHashPayload(value as unknown as GenerationBomBasis));
      if (value.basisHash !== expected) errors.push('basisHash does not match the versioned BOM basis payload');
    } catch (error) {
      errors.push(`basisHash could not be verified: ${(error as Error).message}`);
    }
  }
  return errors;
}

/** Compare a frozen BOM link with the current active generation procurement rows. */
export function evaluateGenerationBomBasisAlignment(
  basis: GenerationBomBasis,
  plan: GenerationBomBasisPlan | null | undefined,
): GenerationBomBasisAlignment {
  const basisErrors = validateGenerationBomBasis(basis);
  if (basisErrors.length > 0) {
    return {
      alignmentStatus: 'modified',
      currentPlanHash: null,
      alignmentNotes: [`The frozen BOM basis failed integrity validation: ${basisErrors[0]}`],
    };
  }
  if (!plan) {
    return {
      alignmentStatus: 'missing',
      currentPlanHash: null,
      alignmentNotes: ['The live supply-chain plan is unavailable.'],
    };
  }

  let current: GenerationBomBasis;
  try {
    current = buildGenerationBomBasis(plan);
  } catch (error) {
    return {
      alignmentStatus: 'missing',
      currentPlanHash: null,
      alignmentNotes: [(error as Error).message],
    };
  }
  if (current.sourcePlanHash === basis.sourcePlanHash) {
    return {
      alignmentStatus: 'aligned',
      currentPlanHash: current.sourcePlanHash,
      alignmentNotes: [],
    };
  }

  const frozenById = new Map(basis.items.map(item => [item.bomItemId, item]));
  const currentById = new Map(current.items.map(item => [item.bomItemId, item]));
  const removed = basis.items.filter(item => !currentById.has(item.bomItemId));
  const added = current.items.filter(item => !frozenById.has(item.bomItemId));
  const changed = basis.items.filter(item => {
    const live = currentById.get(item.bomItemId);
    return live != null && live.itemHash !== item.itemHash;
  });
  const alignmentNotes: string[] = [];
  if (removed.length > 0) alignmentNotes.push(`${removed.length} linked generation BOM item(s) are no longer active.`);
  if (added.length > 0) alignmentNotes.push(`${added.length} active generation BOM item(s) were added after linkage.`);
  if (changed.length > 0) alignmentNotes.push(`${changed.length} linked generation BOM item(s) changed after linkage.`);
  if (alignmentNotes.length === 0) alignmentNotes.push('The active generation BOM projection changed after linkage.');

  return {
    alignmentStatus: removed.length === basis.items.length ? 'missing' : 'modified',
    currentPlanHash: current.sourcePlanHash,
    alignmentNotes,
  };
}
